import React, { useCallback } from 'react'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'

import { makeStyles } from '@material-ui/core/styles'

import * as Helper from '../util/Helper.js'
import TableStructure from './TableStructure'
import { infoTableHeaders } from '../constant.js'
import TotalLikes from '../likes/TotalLikes.js'
import TotalComments from '../single-proposal/Comments/TotalComments.js'
// import SelectUserForInfoTable from './SelectUserForInfoTable.js'
import { GiModernCity } from 'react-icons/gi'
import { BsBarChartSteps, BsFillPeopleFill } from 'react-icons/bs'
import { MdOutlineArchitecture } from 'react-icons/md'
import { FaMinusCircle, FaPlusCircle } from 'react-icons/fa'

const useStyles = makeStyles({
  class: {
    width: '7.5rem',
    fontWeight: 700,
    fontSize: '0.75rem'
  },
  classBox: {
    padding: '6px 4px 4px',
    textAlign: 'center',
    color: '#fff'
  },
  classHolder: {
    paddingTop: 5
  },
  juryRate: {
    padding: '4px 10px 2px',
    backgroundColor: '#e8e8e8',
    color: '#000',
    fontWeight: 'bold',
    fontSize: '0.875rem',
    lineHeight: '1.5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
})

function checkNumberIsFloat(value) {
  return Number(value) === value && value % 1 !== 0
}

function getCompetitionEntryValue(proposal, categoryName) {
  const data =
    !_.isEmpty(proposal.excelData) &&
    proposal.excelData.competitionEntry.EN.length > 0
      ? proposal.excelData.competitionEntry.EN.find(
          element => element.name === categoryName
        )
      : {}
  const value = !_.isEmpty(data) ? data.value : null
  const formattedValue = checkNumberIsFloat(value) ? value.toFixed(2) : value

  return formattedValue
}

function spaceBetweenThousands(value) {
  return value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : ''
}

const getSvgIconForTableHeader = header => {
  switch (header) {
    case 'Kaupunkikuvaan ja kaupunkirakenteeseen sovittaminen':
      return (
        <div style={{ fontSize: 30 }}>
          <GiModernCity />
        </div>
      )
    case 'Kokonaistaloudellisuus ja edellytykset elinkaaritavoitteiden täyttymiseen sekä ekologisuus':
      return (
        <div style={{ fontSize: 25 }}>
          <BsBarChartSteps />
        </div>
      )
    case 'Kulttuurisen vetovoiman vahvistaminen':
      return (
        <div style={{ fontSize: 25 }}>
          <BsFillPeopleFill />
        </div>
      )
    case 'Arkkitehtoniset ja toiminnalliset ratkaisut':
      return (
        <div style={{ fontSize: 32 }}>
          <MdOutlineArchitecture />
        </div>
      )
    default:
      return ''
  }
}

const twoColumns = value => {
  const plusIcon = (
    <div style={{ fontSize: 20 }}>
      <FaPlusCircle color="#00bc00" />
    </div>
  )
  const minusIcon = (
    <div style={{ fontSize: 20 }}>
      <FaMinusCircle color="#f00" />
    </div>
  )

  return (
    <div
      className="d-flex align-items-center justify-content-between"
      style={{ padding: '0 20px' }}
    >
      <div>
        {value.simo === 'plus' ? (
          plusIcon
        ) : value.simo === 'minus' ? (
          minusIcon
        ) : (
          <div style={{ minWidth: 20 }} />
        )}
      </div>
      <div>
        {value.claudia === 'plus' ? (
          plusIcon
        ) : value.claudia === 'minus' ? (
          minusIcon
        ) : (
          <div style={{ minWidth: 20 }} />
        )}
      </div>
    </div>
  )
}

function generateInfoTableColumns() {
  return infoTableHeaders.map(({ header, accessor }) => {
    const headerWithSvg = getSvgIconForTableHeader(header)
    const headerHtml = (
      <div>
        <div>{headerWithSvg}</div>
        <div
          className="d-flex align-items-center justify-content-between"
          style={{ padding: '0 20px' }}
        >
          <p className="mb-0">Simo</p>
          <div>|</div>
          <p className="mb-0">Claudia</p>
        </div>
      </div>
    )

    return {
      Header: headerHtml,
      accessor,
      Cell: ({ cell: { value } }) => twoColumns(value)
    }
  })
}

function generateStatisticsValueObj(value) {
  const statisticsValueObj = {}

  infoTableHeaders.forEach(({ accessor, header }) => {
    statisticsValueObj[accessor] = getCompetitionEntryValue(value, header)
  })

  return statisticsValueObj
}

function generateRateWithPlusOrMinusObj(claudiaData, simoData, proposalId) {
  const statisticsValueObj = {}

  infoTableHeaders.forEach(({ accessor, header }) => {
    // statisticsValueObj[accessor] = getCompetitionEntryValue(value, header)
    statisticsValueObj[accessor] = {
      claudia:
        claudiaData?.proposals?.[proposalId]?.rateWithPlusOrMinus?.[accessor],
      simo: simoData?.proposals?.[proposalId]?.rateWithPlusOrMinus?.[accessor]
    }
  })

  return statisticsValueObj
}

const getUserData = (users, userId) => {
  return users.find(user => user.id === userId)
}

export default function InfoTable({
  Data,
  proposalsOrdered,
  PublicClasses,
  users,
  ratingCriteria
}) {
  const classes = useStyles()
  const { t } = useTranslation('proposals')

  const claudiaData = getUserData(users, '8CV6naz7dDbVB89w0nRFwRwGkMR2')
  const simoData = getUserData(users, 'PehPUNk0t3RyrQTO26EiuOYbxVI2')

  const classValueHtml = useCallback(
    value => {
      return (
        <div className={classes.class}>
          {value && (
            <div
              className={classes.classBox}
              style={{ backgroundColor: value.colour }}
            >
              {value.class}
            </div>
          )}
        </div>
      )
    },
    [classes.class, classes.classBox]
  )

  const renderJuryRate = useCallback(
    value => {
      return value ? (
        <div className={classes.juryRate}>{value.toFixed(2)}</div>
      ) : null
    },
    [classes.juryRate]
  )

  const renderLikesComments = useCallback(proposalId => {
    return proposalId ? (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          marginTop: 10
        }}
      >
        <TotalLikes proposalId={proposalId} style={{ minWidth: 40 }} />
        <TotalComments proposalId={proposalId} style={{ minWidth: 40 }} />
      </div>
    ) : null
  }, [])

  const columns = React.useMemo(
    () => [
      {
        Header: 'Proposals', // Unique key and cannot remove
        columns: [
          {
            Header: t('No.'),
            accessor: 'no'
          },
          {
            Header: t('Pseudonym'),
            accessor: 'pseudonym'
          },
          {
            Header: t('Class'),
            accessor: 'proposalClass',
            disableSorting: true,
            Cell: ({ cell: { value } }) => classValueHtml(value)
          },
          {
            Header: 'R',
            accessor: 'juryRate',
            disableSorting: true,
            Cell: ({ cell: { value } }) => renderJuryRate(value)
          },
          {
            Header: 'Likes & Comments',
            accessor: 'likesAndComments',
            disableSorting: true,
            Cell: ({ cell }) =>
              renderLikesComments(cell?.row?.original?.proposalId)
          }
        ]
      },
      {
        Header: 'Info', // Unique key and cannot remove
        columns: generateInfoTableColumns()
      }
    ],
    [t, classValueHtml, renderJuryRate, renderLikesComments]
  )

  const calculateProposalAverageScore = React.useCallback(
    (ratesWithMultiplier, ratingCriteria) => {
      if (!ratingCriteria) return

      const totalMultiplier = ratingCriteria.reduce((acc, item) => {
        const rateValue = ratesWithMultiplier.rate[item.name]
        return rateValue ? acc + item.multiplier : acc
      }, 0)

      const score = ratingCriteria.reduce((acc, item) => {
        const rateValue = ratesWithMultiplier.rate[item.name]
        const multiplier = item.multiplier
        return rateValue ? acc + rateValue * multiplier : acc
      }, 0)

      const averageScore = totalMultiplier ? score / totalMultiplier : 0

      return averageScore
    },
    []
  )

  const generateArrayOfSingleRowObj = useCallback(() => {
    let singleRowObj = {}
    let statisticsValueObj = {}

    return _.map(Data, (value, key) => {
      const { number, name } = value
      const proposalClass = Helper.getProposalClass(
        key,
        proposalsOrdered,
        PublicClasses
      )
      // const proposalAllRate = Helper.getProposalAllRate(users, key)
      const ratesWithMultiplier = Helper.getProposalAllRateWithMultiplier(
        users,
        key,
        ratingCriteria
      )
      const averageScore = calculateProposalAverageScore(
        ratesWithMultiplier,
        ratingCriteria
      )

      let proposalNoNameObj = {
        no: number,
        pseudonym: name,
        proposalId: key
      }

      // Add class into 'Class' column
      if (proposalClass) {
        proposalNoNameObj = {
          ...proposalNoNameObj,
          proposalClass: proposalClass
        }
      }

      // Add rate into 'R' column
      if (!_.isEmpty(ratesWithMultiplier.rate)) {
        proposalNoNameObj = {
          ...proposalNoNameObj,
          juryRate: claudiaData?.proposals?.[key]?.rate?.overall
        }
      }

      statisticsValueObj = generateRateWithPlusOrMinusObj(
        claudiaData,
        simoData,
        key
      )

      // Combine proposal's no, name and statistic value object
      singleRowObj = {
        ...proposalNoNameObj,
        ...statisticsValueObj
      }
      // console.log('singleRowObj:', singleRowObj)

      return singleRowObj
    })
  }, [
    Data,
    PublicClasses,
    calculateProposalAverageScore,
    claudiaData,
    proposalsOrdered,
    ratingCriteria,
    simoData,
    users
  ])

  // Only re-render when data in table is changed
  const data = React.useMemo(() => generateArrayOfSingleRowObj(), [
    generateArrayOfSingleRowObj
  ])

  return (
    <>
      <TableHeaderDescription />
      <TableStructure
        columns={columns}
        data={data}
        disableMultiSort={true}
        proposals={Data}
      />
    </>
  )
}

function TableHeaderDescription() {
  return (
    <div>
      <div className="d-flex align-items-center" style={{ gap: 10 }}>
        <div style={{ fontSize: 25 }}>
          <GiModernCity />
        </div>
        <p> = Kaupunkikuvaan ja kaupunkirakenteeseen sovittaminen</p>
      </div>
      <div className="d-flex align-items-center" style={{ gap: 10 }}>
        <div style={{ fontSize: 22 }}>
          <BsBarChartSteps />
        </div>
        <p>
          {' '}
          = Kokonaistaloudellisuus ja edellytykset elinkaaritavoitteiden
          täyttymiseen sekä ekologisuus
        </p>
      </div>
      <div className="d-flex align-items-center" style={{ gap: 10 }}>
        <div style={{ fontSize: 22 }}>
          <BsFillPeopleFill />
        </div>
        <p> = Kulttuurisen vetovoiman vahvistaminen</p>
      </div>
      <div className="d-flex align-items-center" style={{ gap: 10 }}>
        <div style={{ fontSize: 27 }}>
          <MdOutlineArchitecture />
        </div>
        <p> = Arkkitehtoniset ja toiminnalliset ratkaisut</p>
      </div>
    </div>
  )
}
